import React from 'react';
import {
  Loader2,
  User as UserIcon,
  Plus,
  RefreshCw,
  Clock,
  FileText,
  Brain,
  Hash,
  Pin,
  PinOff,
  Share2,
  Copy,
  Trash2,
} from 'lucide-react';
import type { PlaygroundUser } from '../../api/playgroundUsers';
import { getTierStyle } from '../TabPanels';
import { formatDateKo } from '../../utils/date';

export interface PlaygroundUserListProps {
  users: PlaygroundUser[];
  usersLoading: boolean;
  selectedUserId: string | null;
  onSelectUser: (userId: string) => void;
  onCreateUser: () => void;
  onRefreshUsers: () => void;
  onDeleteUser: (userId: string, e: React.MouseEvent) => void;
  onCloneUser: (userId: string, e: React.MouseEvent) => void;
  onTogglePin: (userId: string, settings: PlaygroundUser['settings'], e: React.MouseEvent) => void;
  onOpenSharing: (userId: string, settings: PlaygroundUser['settings'], e: React.MouseEvent) => void;
}

const PlaygroundUserList: React.FC<PlaygroundUserListProps> = React.memo(({
  users,
  usersLoading,
  selectedUserId,
  onSelectUser,
  onCreateUser,
  onRefreshUsers,
  onDeleteUser,
  onCloneUser,
  onTogglePin,
  onOpenSharing,
}) => {
  return (
    <>
      {/* User section header */}
      <div className="p-4 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
        <h3 className="font-semibold text-slate-800">사용자</h3>
        <div className="flex gap-2">
          <button
            onClick={onRefreshUsers}
            data-testid="refresh-users"
            className="p-2 text-slate-500 hover:bg-slate-200 rounded-lg transition-colors"
            title="새로고침"
          >
            <RefreshCw size={16} className={usersLoading ? 'animate-spin' : ''} />
          </button>
          <button
            onClick={onCreateUser}
            data-testid="create-user"
            className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            title="새 사용자 생성"
          >
            <Plus size={16} />
          </button>
        </div>
      </div>

      {/* User list */}
      <div className="flex-1 overflow-y-auto" style={{ maxHeight: selectedUserId ? '40%' : undefined }}>
        {usersLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 size={24} className="animate-spin text-slate-400" />
          </div>
        ) : users.length === 0 ? (
          <div className="text-center py-8 text-slate-400">
            <UserIcon size={32} className="mx-auto mb-2 opacity-50" />
            <p className="text-sm">사용자가 없습니다</p>
            <button onClick={onCreateUser} className="mt-2 text-blue-600 hover:underline text-sm">
              새 사용자 만들기
            </button>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {users.map((user) => (
              <div
                key={user.user_id}
                data-testid="user-item"
                onClick={() => onSelectUser(user.user_id)}
                className={`p-3 cursor-pointer transition-colors group ${
                  selectedUserId === user.user_id
                    ? 'bg-blue-50 border-l-2 border-blue-500'
                    : user.settings?.is_pinned
                      ? 'bg-amber-50/50 hover:bg-amber-50'
                      : 'hover:bg-slate-50'
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded shrink-0 ${getTierStyle(user.tier?.id)}`}>
                        {user.tier?.id || 'free'}
                      </span>
                      {user.settings?.is_pinned && <Pin size={10} className="text-amber-500 shrink-0" />}
                      <span className="flex items-center gap-1 text-xs text-slate-400">
                        <Clock size={10} />
                        {formatDateKo(user.updated_at)}
                      </span>
                    </div>
                    <div className="font-medium text-sm text-slate-800 truncate">
                      {(user.user as Record<string, string>)?.name || '이름 없음'}
                    </div>
                    <div className="font-mono text-[10px] text-slate-500 break-all leading-tight">{user.user_id}</div>
                    <div className="flex items-center gap-4 mt-1 text-xs text-slate-400">
                      <span className="flex items-center gap-1">
                        <Brain size={10} />
                        {user.usage?.memory_count || 0}
                      </span>
                      <span className="flex items-center gap-1">
                        <FileText size={10} />
                        {user.usage?.document_count || 0}
                      </span>
                      <span className="flex items-center gap-1">
                        <Hash size={10} />
                        {user.usage?.thread_count || 0}
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={(e) => onTogglePin(user.user_id, user.settings, e)}
                      data-testid={user.settings?.is_pinned ? 'unpin-user' : 'pin-user'}
                      className={`p-1 ${user.settings?.is_pinned ? 'text-amber-500 hover:text-amber-600' : 'text-slate-300 hover:text-amber-500'}`}
                      title={user.settings?.is_pinned ? '고정 해제' : '고정'}
                    >
                      {user.settings?.is_pinned ? <PinOff size={14} /> : <Pin size={14} />}
                    </button>
                    <button
                      onClick={(e) => onOpenSharing(user.user_id, user.settings, e)}
                      data-testid="share-user"
                      className="p-1 text-slate-300 hover:text-blue-500"
                      title="공유"
                    >
                      <Share2 size={14} />
                    </button>
                    <button
                      onClick={(e) => onCloneUser(user.user_id, e)}
                      data-testid="clone-user"
                      className="p-1 text-slate-300 hover:text-green-600"
                      title="복제 (디버깅용)"
                    >
                      <Copy size={14} />
                    </button>
                    <button
                      onClick={(e) => onDeleteUser(user.user_id, e)}
                      data-testid="delete-user"
                      className="p-1 text-slate-300 hover:text-red-500"
                      title="삭제"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
});
PlaygroundUserList.displayName = 'PlaygroundUserList';

export default PlaygroundUserList;
